import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleCheckIcon, Loader2, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'

import { useModalState } from '@/hooks/use-modal-state.ts'
import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import { confirm } from '@/components/confirmation.tsx'
import { Empty } from '@/components/empty.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { Uploader, UploadingFile } from '@/components/uploader.tsx'
import { AttachmentList } from '@/features/attachments/attachment-list.tsx'
import {
  useCreateAttachment,
} from '@/features/attachments/hooks/use-create-attachment.ts'
import { useDeleteAttachment } from '@/features/attachments/hooks/use-delete-attachment.ts'
import {
  getDocumentAttachmentsQueryOptions,
  useGetDocumentAttachments,
} from '@/features/attachments/hooks/use-get-document-attachments.ts'
import { useUpdateAttachment } from '@/features/attachments/hooks/use-update-attachment.ts'
import { Attachment } from '@/features/attachments/types.ts'
import { Document } from '@/features/documents/types.ts'
import { downloadFile } from '@/features/files/hooks/use-download-file.ts'

export const AttachmentsTab: React.FC<{ document?: Document }> = ({
  document,
}) => {
  const getDocumentAttachmentsQuery = useGetDocumentAttachments(
    { documentId: document!.id },
    { enabled: !!document }
  )
  const attachments = getDocumentAttachmentsQuery?.data?.data
  const isEmpty =
    getDocumentAttachmentsQuery.isSuccess && attachments?.length === 0

  const createAttachment = useCreateAttachment()
  const handleFileUploaded = async (uploadingFile: UploadingFile) => {
    await createAttachment.mutateAsync({
      file_id: uploadingFile.fileId!,
      document_id: document!.id,
      name: uploadingFile.file.name,
    })
    await revalidateAttachments()
  }

  const handleFileRemoved = (uploadingFile: UploadingFile) =>
    new Promise<void>((resolve, reject) =>
      confirm({
        title: 'Elimina allegato',
        content: "Sei sicuro di voler eliminare l'allegato?",
        okButtonVariant: 'destructive',
        okText: 'Elimina',
        async onOk() {
          // If already created, delete the attachment
          if (uploadingFile.attachmentId) {
            await deleteAttachment.mutateAsync({
              attachmentId: uploadingFile.attachmentId,
            })
            revalidateAttachments()
          }
          resolve()
        },
        onCancel: () => reject(),
      })
    )

  const [selectedAttachmentForRename, setSelectedAttachmentForRename] =
    useState<Attachment | null>(null)
  const renameAttachmentDialogCtrl = useModalState()
  const handleRenameAttachmentBtnClick = (attachment: Attachment) => {
    setSelectedAttachmentForRename(attachment)
    renameAttachmentDialogCtrl.open()
  }
  const queryClient = useQueryClient()
  const revalidateAttachments = () =>
    queryClient.invalidateQueries(
      getDocumentAttachmentsQueryOptions({ documentId: document!.id })
    )
  const handleAttachmentRenamed = () => revalidateAttachments()

  const deleteAttachment = useDeleteAttachment({
    onSuccess() {
      toast('Allegato rimosso', { icon: <CircleCheckIcon /> })

      return revalidateAttachments()
    },
  })

  const handleDeleteAttachmentBtnClick = (attachment: Attachment) => {
    setTimeout(() =>
      confirm({
        title: 'Elimina allegato',
        content: "Sei sicuro di voler eliminare l'allegato?",
        okButtonVariant: 'destructive',
        okText: 'Elimina',
        async onOk() {
          await deleteAttachment.mutateAsync({ attachmentId: attachment.id })
        },
      })
    )
  }

  return (
    <>
      <QuerySpinner
        query={getDocumentAttachmentsQuery}
        wrapperClassName="py-10"
      >
        <Uploader
          onFileUploaded={handleFileUploaded}
          onFileRemoved={handleFileRemoved}
        >
          {isEmpty ? (
            <Empty
              className="py-4"
              title={
                <>
                  Nessun allegato disponibile
                  <br />
                  Clicca qui o trascina i file per caricare gli allegati
                </>
              }
            />
          ) : null}
        </Uploader>

        <AttachmentList
          attachments={attachments}
          actions={(attachment) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-500 hover:text-gray-900"
                >
                  <span className="sr-only">Apri opzioni</span>
                  <MoreVertical className="size-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() =>
                    downloadFile({
                      fileId: attachment.file_id,
                      downloadName: attachment.name,
                    })
                  }
                >
                  Download<span className="sr-only">, {attachment.name}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleRenameAttachmentBtnClick(attachment)}
                >
                  Rinomina<span className="sr-only">, {attachment.name}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDeleteAttachmentBtnClick(attachment)}
                >
                  Elimina<span className="sr-only">, {attachment.name}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />
      </QuerySpinner>

      <RenameAttachmentDialog
        attachment={selectedAttachmentForRename}
        isOpen={renameAttachmentDialogCtrl.isOpen}
        onClose={renameAttachmentDialogCtrl.close}
        onRenamed={handleAttachmentRenamed}
      />
    </>
  )
}

function RenameAttachmentDialog({
  attachment,
  isOpen,
  onClose,
  onRenamed,
}: {
  attachment?: Attachment | null
  isOpen: boolean
  onClose?: () => void | Promise<void>
  onRenamed?: () => void | Promise<void>
}) {
  const form = useSmartForm<Partial<Attachment>>()
  const updateAttachment = useUpdateAttachment()
  useEffect(() => {
    form.reset(attachment ?? {})
  }, [attachment])

  const onSubmit = async (attachment: Partial<Attachment>) => {
    await updateAttachment.mutateAsync({
      attachmentId: attachment.id!,
      name: attachment.name,
    })
    onRenamed?.()
    onClose?.()
    toast('Allegato rinominato', { icon: <CircleCheckIcon /> })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rinomina Allegato</DialogTitle>
          <DialogDescription>
            Make changes to your profile here. Click save when you're done.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription></FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="submit"
                disabled={
                  !form.formState.isValid || form.formState.isSubmitting
                }
              >
                {updateAttachment.isPending && (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                )}
                Salva
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
